/**
 * `scribeErrors.ts` — shared error-shape detection for the AI scribe data
 * layer (`useLiveTranscription`, `useFinalizeScribe`, `CallSessionProvider`).
 */
import { getStatus, isOperationOutcome } from '@medplum/core'
import { RECORD_DURATION_ENCOUNTER_MISSING } from '@/model/middleware-types'

/**
 * Thrown by `useFinalizeScribe` when `/generate-draft` fails for a transient
 * (non-404) reason AFTER the transcript was durably saved by `/finalize`. The
 * caller catches this to keep the call alive and offer a retry — the draft is a
 * pure, repeatable read, so re-running it against the same saved transcript is
 * always safe.
 */
export class ScribeDraftGenerationError extends Error {
  constructor() {
    super('AI draft generation failed')
    this.name = 'ScribeDraftGenerationError'
  }
}

/**
 * Detect "scribe session id not found" (HTTP 404) responses from the
 * middleware.
 *
 * Why: the scribe session lives in a Redis-backed store on the middleware
 * (`packages/middleware/src/scribe/transcription/redis-transcription.ts`),
 * which survives a middleware restart. But the session still has a 24h TTL and
 * can be explicitly ended/expired, so the frontend may hold a `scribeSessionId`
 * the server no longer recognizes. Every queued transcript batch (or finalize /
 * generate-draft call) then 404s and floods the console with errors.
 *
 * The contracts client at `packages/contracts/src/client.ts:58` re-throws the
 * response body as-is. The middleware's `AllExceptionsFilter`
 * (`packages/middleware/src/filters/all-exceptions.filter.ts:24-30`) serializes
 * an `HttpException`'s `getResponse()` directly, so for a Nest
 * `NotFoundException` we receive a plain `HttpException` JSON shape, NOT a FHIR
 * OperationOutcome:
 *
 *   { "statusCode": 404, "error": "Not Found", "message": "Scribe session ... not found" }
 *
 * We therefore check three shapes (union of what both call sites historically
 * handled — this is the reconciled, most-complete detection):
 *
 *   1. FHIR OperationOutcome (covers any middleware path that hands back a
 *      normalized outcome — currently the catch-all branch in the filter at
 *      line 33-46).
 *   2. `statusCode` on the raw body (Nest `HttpException` default shape).
 *   3. `status` on the raw error (safety net for callers that bypass the
 *      contracts client via `middleware.raw.path(...)`).
 */
/**
 * Detect the middleware's "no transcript to persist" rejection from /finalize —
 * the doctor ended a scribe session that captured nothing (muted mic / LTT never
 * attached). HTTP 400 with a stable message marker; the caller treats it as a
 * soft outcome (open an empty note) rather than a retryable error.
 */
export function isEmptyTranscriptError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { statusCode?: unknown; status?: unknown; message?: unknown }
  const status = e.statusCode ?? e.status
  const message = Array.isArray(e.message)
    ? e.message.join(' ')
    : typeof e.message === 'string'
      ? e.message
      : ''
  return status === 400 && /no transcript to persist/i.test(message)
}

/**
 * PD-1671 — `/duration` found no Encounter to record on. A 404 alone is not
 * enough: a route that does not exist (web deployed ahead of middleware, or a
 * later rename) is a 404 too, and silently skipping that loses every visit's
 * duration with no trace. Only the handler's own 404 carries the marker.
 */
export function isDurationEncounterMissing(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { statusCode?: unknown; status?: unknown; message?: unknown }
  const status = e.statusCode ?? e.status
  return (
    status === 404 &&
    typeof e.message === 'string' &&
    e.message.includes(RECORD_DURATION_ENCOUNTER_MISSING)
  )
}

/**
 * HTTP 409 in either transport shape: oRPC's `ORPCError` carries `status`, while
 * the legacy contracts client re-throws Nest's `HttpException` body (`statusCode`).
 */
function isConflict409(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { statusCode?: unknown; status?: unknown }
  return e.statusCode === 409 || e.status === 409
}

/**
 * PD-1378 — the middleware refuses a second `/generate-draft` while one is
 * already running for the encounter. Not a failure: the in-flight run still
 * produces the draft, so the caller must NOT enter the retry/error state.
 */
export function isDraftAlreadyGenerating(err: unknown): boolean {
  return isConflict409(err)
}

/** PD-1439 — `/join-claim` rejected because another tab holds this encounter. */
export function isJoinClaimConflict(err: unknown): boolean {
  return isConflict409(err)
}

export function isStaleScribeSession404(err: unknown): boolean {
  if (isOperationOutcome(err)) {
    try {
      return getStatus(err) === 404
    } catch {
      return false
    }
  }
  if (err && typeof err === 'object') {
    const e = err as { statusCode?: unknown; status?: unknown }
    if (e.statusCode === 404 || e.status === 404) {
      return true
    }
  }
  return false
}
